import { InterventionActualisation } from '../../Services/intervention-actualisation';
import { Component, DestroyRef, effect, input, inject, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { Installation as InstallationService } from '../../Services/installation';
import { TicketState } from '../../Services/ticket-state';
import { compterInterventions, CompteursInterventions } from '../../Services/interventions';

export type TicketEtat = (typeof TicketState)[keyof typeof TicketState];

export type TicketResume = {
  label: string;
  count: number;
  etat: TicketEtat;
};

@Component({
  selector: 'app-installation-ticket-panel',
  imports: [RouterLink],
  templateUrl: './installation-ticket-panel.html',
  styleUrl: './installation-ticket-panel.css',
})
export class InstallationTicketPanelComponent {
  private readonly actualisationInterventions = inject(InterventionActualisation);
  readonly installationId = input<number | null>(null);

  private readonly installationService = inject(InstallationService);
  private readonly destroyRef = inject(DestroyRef);
  private requeteId = 0;

  readonly erreurTickets = signal(false);
  readonly chargement = signal(false);
  readonly total = signal(0);
  readonly tickets = signal<TicketResume[]>([]);
  readonly chargementInterventions = signal(false);
  readonly erreurInterventions = signal(false);
  readonly compteursInterventions = signal<CompteursInterventions | null>(null);

  constructor() {
    effect(() => {
      this.actualisationInterventions.revision();
      const id = this.installationId();
      // Les deux requêtes partagent une génération : changer d'installation invalide les deux réponses.
      const requete = ++this.requeteId;
      this.total.set(0);
      this.tickets.set([]);
      this.chargement.set(false);
      this.erreurTickets.set(false);
      this.compteursInterventions.set(null);
      this.chargementInterventions.set(false);
      this.erreurInterventions.set(false);
      if (id != null) {
        void this.chargerTickets(id, requete);
        void this.chargerInterventions(id, requete);
      }
    });
  }

  private requeteValide(requete: number): boolean {
    return requete === this.requeteId && !this.destroyRef.destroyed;
  }

  reessayerTickets(): void {
    const id = this.installationId();
    if (id != null && !this.chargement()) void this.chargerTickets(id, this.requeteId);
  }

  private async chargerTickets(id: number, requete: number) {
    this.chargement.set(true);
    this.erreurTickets.set(false);
    try {
      const stats = await this.installationService.getInstallationTicketStats(id);
      if (!this.requeteValide(requete)) return;
      if (!stats) throw new Error('Tickets indisponibles');
      this.total.set(stats.total);
      const etats = stats?.etats ?? {};
      const lignes: TicketResume[] = [
        { label: 'Nouveau', count: etats.nouveau ?? 0, etat: TicketState.nouveau },
        { label: 'En cours', count: etats.encours ?? 0, etat: TicketState.encours },
        { label: 'En attente', count: etats.enattente ?? 0, etat: TicketState.enattente },
        { label: 'Résolu', count: etats.resolu ?? 0, etat: TicketState.resolu },
      ];
      this.tickets.set(lignes);
    } catch {
      if (!this.requeteValide(requete)) return;
      this.erreurTickets.set(true);
      this.total.set(0);
      this.tickets.set([]);
    } finally {
      if (this.requeteValide(requete)) this.chargement.set(false);
    }
  }

  reessayerInterventions(): void {
    const id = this.installationId();
    if (id != null && !this.chargementInterventions()) {
      void this.chargerInterventions(id, this.requeteId);
    }
  }

  private async chargerInterventions(id: number, requete: number): Promise<void> {
    this.chargementInterventions.set(true);
    this.erreurInterventions.set(false);
    this.compteursInterventions.set(null);
    try {
      const response = await this.installationService.getInstallationInterventions(id);
      if (!this.requeteValide(requete)) return;
      if (!response) throw new Error('Interventions indisponibles');
      this.compteursInterventions.set(compterInterventions(response));
    } catch {
      if (this.requeteValide(requete)) this.erreurInterventions.set(true);
    } finally {
      if (this.requeteValide(requete)) this.chargementInterventions.set(false);
    }
  }
}
